import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Tooltip, message } from 'antd';
import { observer } from 'mobx-react';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../../i18n';
import { PlanMemberMark } from '../../../components/PlanMemberMark';
import { planMemberTier } from '../pricing/pricingUtils';
import { useCurrentAccount } from '../layout/currentAccountContext';
import { useMerchantRealtime } from '../layout/merchantRealtimeContext';
import { useSelectedShop } from '../layout/selectedShopContext';
import { QuickOutreachModal } from '../components/QuickOutreachModal';
import { getAccountQuota, getDmPendingReplyCount, getEmailPendingReplyCount, getInfluencerFunnel } from '../../../store/request';
import type { AccountQuota, AccountTime, MailQuota } from '../../../types/account';
import type { InfluencerFunnel } from '../../../types/influencer';
import type { SellerAuthShop } from '../../../types/seller';
import {
  CarryOutOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CommentOutlined,
  DesktopOutlined,
  MailOutlined,
  MessageOutlined,
  QuestionCircleOutlined,
  SearchOutlined,
  SendOutlined,
  ShopOutlined,
  SolutionOutlined,
  StarOutlined,
  ThunderboltOutlined,
  UngroupOutlined,
} from '@ant-design/icons';

type StatCardTone = 'violet' | 'sky' | 'emerald' | 'amber';

const statCardToneClass: Record<
  StatCardTone,
  {
    topBar: string;
    blob: string;
    icon: string;
    progress: string;
    chip: string;
    chipDot: string;
    value: string;
  }
> = {
  violet: {
    topBar: 'from-[#3b4dd7] to-[#596ae8]',
    blob: 'bg-[#596ae8]/10',
    icon: 'bg-[#596ae8]/[0.12] text-[#596ae8]',
    progress: 'from-[#3b4dd7] to-[#596ae8]',
    chip: 'bg-[#596ae8]/10 text-[#596ae8]',
    chipDot: 'bg-[#596ae8]/35',
    value: 'text-[#3b4dd7]',
  },
  sky: {
    topBar: 'from-[#0284c7] to-[#0ea5e9]',
    blob: 'bg-sky-500/10',
    icon: 'bg-sky-500/10 text-sky-600',
    progress: 'from-[#0284c7] to-[#0ea5e9]',
    chip: 'bg-sky-500/10 text-sky-600',
    chipDot: 'bg-sky-500/35',
    value: 'text-sky-600',
  },
  emerald: {
    topBar: 'from-[#059669] to-[#10b981]',
    blob: 'bg-emerald-500/10',
    icon: 'bg-emerald-500/10 text-emerald-600',
    progress: 'from-[#059669] to-[#10b981]',
    chip: 'bg-emerald-500/10 text-emerald-600',
    chipDot: 'bg-emerald-500/35',
    value: 'text-emerald-600',
  },
  amber: {
    topBar: 'from-[#d97706] to-[#f59e0b]',
    blob: 'bg-amber-500/10',
    icon: 'bg-amber-500/10 text-amber-600',
    progress: 'from-[#d97706] to-[#f59e0b]',
    chip: 'bg-amber-500/10 text-amber-600',
    chipDot: 'bg-amber-500/35',
    value: 'text-amber-600',
  },
};

type WorkbenchStatCardProps = {
  icon: React.ReactNode;
  title: string;
  tone: StatCardTone;
  children: React.ReactNode;
};

type FunnelMetaTone = 'muted' | 'warn' | 'success';
type FunnelStepVariant = 'default' | 'success';
type FunnelStep = {
  key: string;
  icon: React.ReactNode;
  value: string;
  label: string;
  meta: string;
  metaTone: FunnelMetaTone;
  variant?: FunnelStepVariant;
};

function formatFunnelCount(value?: number) {
  if (value == null || !Number.isFinite(value)) return '—';
  return Math.trunc(value).toLocaleString('en-US');
}

function formatQuotaResetAt(value?: AccountTime | null) {
  if (value == null) return '';
  if (typeof value === 'string') {
    const text = value.trim();
    if (!text) return '';
    const parsed = dayjs(text);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD HH:mm') : '';
  }
  if (typeof value.seconds !== 'number' || value.seconds <= 0) return '';
  const millis = value.seconds * 1000 + Math.floor((value.nanos ?? 0) / 1e6);
  const parsed = dayjs(millis);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD HH:mm') : '';
}

function formatFunnelRate(part?: number, whole?: number) {
  if (part == null || whole == null) return '—';
  if (whole <= 0) return '0';
  return ((part / whole) * 100).toLocaleString('en-US', { maximumFractionDigits: 2 });
}

const funnelMetaToneClass: Record<FunnelMetaTone, string> = {
  muted: 'text-slate-400',
  warn: 'text-[#e85d4c]',
  success: 'text-green-600',
};

const funnelIconWrapClass: Record<FunnelStepVariant, string> = {
  default: 'bg-[#f0f2ff] text-[#596ae8]',
  success: 'bg-emerald-50 text-green-500',
};

const funnelValueClass: Record<FunnelStepVariant, string> = {
  default: 'text-slate-800',
  success: 'text-green-600',
};

type QuickEntryItem = {
  key: string;
  label: string;
  icon: React.ReactNode;
  path: string;
};

function WorkbenchStatCard({ icon, title, tone, children }: WorkbenchStatCardProps) {
  const toneStyle = statCardToneClass[tone];
  return (
    <article className="relative overflow-hidden rounded-[14px] bg-white px-[18px] pb-4 pt-[18px] shadow-[0_1px_3px_rgba(3, 0, 71, 0.09)]">
      <div
        className={`absolute inset-x-0 top-0 z-[2] h-[3px] bg-gradient-to-r ${toneStyle.topBar}`}
        aria-hidden
      />
      <div
        className={`pointer-events-none absolute -right-7 -top-7 size-24 rounded-full ${toneStyle.blob}`}
        aria-hidden
      />
      <div className="relative z-[1] mb-3.5 flex items-start justify-between gap-3">
        <span
          className={`inline-flex size-9 items-center justify-center rounded-[10px] text-[17px] ${toneStyle.icon}`}
        >
          {icon}
        </span>
        <span className="pt-1.5 text-right text-sm font-medium leading-snug text-slate-500">
          {title}
        </span>
      </div>
      {children}
    </article>
  );
}

function periodQuotaPercent(quota?: MailQuota) {
  if (!quota || quota.unlimited === true || quota.total <= 0) return 0;
  return Math.min(100, Math.round((quota.used / quota.total) * 100));
}

function WorkbenchPeriodQuota({
  icon,
  title,
  tone,
  quota,
}: {
  icon: React.ReactNode;
  title: string;
  tone: StatCardTone;
  quota?: MailQuota;
}) {
  const { t } = useTranslation();
  const unlimited = quota?.unlimited === true;
  const totalText = !quota ? '—' : unlimited ? t('workbench.unlimited') : formatFunnelCount(quota.total);
  return (
    <WorkbenchStatCard icon={icon} title={title} tone={tone}>
      <p
        className={`relative z-[1] mb-3 mt-0 text-[32px] font-bold leading-none tracking-tight ${statCardToneClass[tone].value}`}
      >
        {formatFunnelCount(quota?.used)}
      </p>
      {quota ? <WorkbenchProgress percent={periodQuotaPercent(quota)} tone={tone} /> : null}
      <div className="relative z-[1] flex flex-wrap items-center gap-2">
        <span
          className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs leading-snug ${statCardToneClass[tone].chip}`}
        >
          {unlimited
            ? t('workbench.unlimited')
            : t('workbench.mail_remain', { remain: formatFunnelCount(quota?.remaining) })}
        </span>
        {unlimited ? null : (
          <span className="text-xs leading-normal text-slate-400">
            / {totalText}
            {quota ? `（${t('workbench.addon_included', { extra: formatFunnelCount(quota.addonTotal) })}）` : ''}
          </span>
        )}
      </div>
    </WorkbenchStatCard>
  );
}

function WorkbenchProgress({ percent, tone }: { percent: number; tone: StatCardTone }) {
  const progressClass = statCardToneClass[tone].progress;
  return (
    <div
      className="relative z-[1] mb-2.5 h-[5px] overflow-hidden rounded-full bg-[#e8ecf4]"
      role="progressbar"
      aria-valuenow={percent}
    >
      <div
        className={`h-full rounded-full bg-gradient-to-r ${progressClass}`}
        style={{ width: `${Math.min(100, Math.max(0, percent))}%` }}
      />
    </div>
  );
}

function WorkbenchFunnelStep({ step }: { step: FunnelStep }) {
  const variant = step.variant ?? 'default';
  return (
    <div
      className="flex min-w-[108px] flex-1 cursor-default flex-col items-center gap-1.5 rounded-xl bg-transparent px-2 py-2.5 pb-3 text-center transition duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] hover:bg-[#596ae8]/[0.06] max-[900px]:min-w-[120px]"
      aria-label={`${step.label} ${step.value}`}
    >
      <span
        className={`inline-flex size-12 shrink-0 items-center justify-center rounded-full text-xl ${funnelIconWrapClass[variant]}`}
      >
        {step.icon}
      </span>
      <span
        className={`text-[22px] font-bold leading-tight tracking-tight ${funnelValueClass[variant]}`}
      >
        {step.value}
      </span>
      <span className="text-[13px] font-medium leading-snug text-slate-700">{step.label}</span>
      <span className={`text-xs leading-snug ${funnelMetaToneClass[step.metaTone]}`}>
        {step.meta}
      </span>
    </div>
  );
}

function WorkbenchSectionHeader({
  icon,
  title,
  action,
}: {
  icon: React.ReactNode;
  title: string;
  action?: React.ReactNode;
}) {
  return (
    <header className="flex items-center justify-between gap-4">
      <div className="flex min-w-0 items-center gap-2.5">
        <span className="-mt-0.5 inline-flex shrink-0 text-base text-[#4e5fe2]" aria-hidden>
          {icon}
        </span>
        <h2 className="m-0 text-base font-semibold leading-snug text-slate-800">{title}</h2>
      </div>
      {action}
    </header>
  );

}

const SHOP_REGION_KEY: Record<string, string> = {
  VN: 'store.market_vn',
  TH: 'store.market_th',
  ID: 'store.market_id',
  MY: 'store.market_my',
  PH: 'store.market_ph',
  SG: 'store.market_sg'
};

function workbenchShopLine(shop: SellerAuthShop | undefined, t: (key: string, options?: Record<string, string>) => string) {
  if (!shop) return t('layout.no_shop');
  const name = shop.shopName?.trim() || '—';
  const regionKey = SHOP_REGION_KEY[(shop.shopRegion ?? '').trim().toUpperCase()];
  if (!regionKey) return name;
  return t('workbench.shop_line', { region: t(regionKey), name });
}

export const Workbench = observer(() => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const account = useCurrentAccount();
  const { shopId, shops } = useSelectedShop();
  const selectedShop = shops.find((shop) => shop.shopId === shopId);
  const [quota, setQuota] = useState<AccountQuota | null>(null);
  const [funnel, setFunnel] = useState<InfluencerFunnel | null>(null);
  const [pendingReplyCount, setPendingReplyCount] = useState<number | null>(null);
  const [emailPendingCount, setEmailPendingCount] = useState<number | null>(null);
  const [outreachOpen, setOutreachOpen] = useState(false);
  const shopIdRef = useRef(shopId);
  const emailLockRef = useRef({ running: false, again: false });
  const dmLockRef = useRef({ running: false, again: false });
  shopIdRef.current = shopId;

  const scheduleEmailPending = () => {
    const lock = emailLockRef.current;
    const run = () => {
      const currentShop = shopIdRef.current;
      if (!currentShop) return;
      if (lock.running) {
        lock.again = true;
        return;
      }
      lock.running = true;
      getEmailPendingReplyCount({ shopId: currentShop })
        .then((res) => {
          if (shopIdRef.current !== currentShop || res.code !== 200 || typeof res.content !== 'number') return;
          setEmailPendingCount(res.content);
        })
        .catch(() => undefined)
        .finally(() => {
          lock.running = false;
          if (lock.again) {
            lock.again = false;
            run();
          }
        });
    };
    run();
  };

  const scheduleDmPending = () => {
    const lock = dmLockRef.current;
    const run = () => {
      const currentShop = shopIdRef.current;
      if (!currentShop) return;
      if (lock.running) {
        lock.again = true;
        return;
      }
      lock.running = true;
      getDmPendingReplyCount({ shopId: currentShop })
        .then((res) => {
          if (shopIdRef.current !== currentShop || res.code !== 200 || typeof res.content !== 'number') return;
          setPendingReplyCount(res.content);
        })
        .catch(() => undefined)
        .finally(() => {
          lock.running = false;
          if (lock.again) {
            lock.again = false;
            run();
          }
        });
    };
    run();
  };

  useMerchantRealtime((event) => {
    if (event.event === 'EMAIL_NEW_MESSAGE' || event.event === 'EMAIL_SOCKET_RECONNECTED') {
      scheduleEmailPending();
      return;
    }
    if (event.event === 'DM_SOCKET_RECONNECTED') {
      scheduleDmPending();
      return;
    }
    if (event.event !== 'DM_NEW_MESSAGE') return;
    const currentShop = shopIdRef.current;
    if (currentShop && event.shopId != null && String(event.shopId) === currentShop) scheduleDmPending();
  });

  useEffect(() => {
    if (!shopId) {
      setFunnel(null);
      return;
    }
    let cancelled = false;
    getInfluencerFunnel({ shopId })
      .then((res) => {
        if (cancelled) return;
        if (res.code !== 200) {
          message.error(res.message || t('workbench.funnel_load_failed'));
          setFunnel(null);
          return;
        }
        setFunnel(res.content ?? null);
      })
      .catch(() => {
        if (!cancelled) message.error(t('workbench.funnel_load_failed'));
      });
    return () => {
      cancelled = true;
    };
  }, [shopId, t]);

  useEffect(() => {
    if (!shopId) {
      setPendingReplyCount(null);
      return;
    }
    let cancelled = false;
    getDmPendingReplyCount({ shopId })
      .then((res) => {
        if (cancelled) return;
        if (res.code !== 200 || typeof res.content !== 'number') {
          if (res.code !== -1) message.error(res.message || t('message.pending_reply_failed'));
          setPendingReplyCount(null);
          return;
        }
        setPendingReplyCount(res.content);
      })
      .catch(() => {
        if (!cancelled) message.error(t('message.pending_reply_failed'));
      });
    return () => {
      cancelled = true;
    };
  }, [shopId, t]);

  useEffect(() => {
    if (!shopId) {
      setEmailPendingCount(null);
      return;
    }
    let cancelled = false;
    getEmailPendingReplyCount({ shopId })
      .then((res) => {
        if (cancelled) return;
        if (res.code !== 200 || typeof res.content !== 'number') {
          if (res.code !== -1) message.error(res.message || t('email.pending_reply_failed'));
          setEmailPendingCount(null);
          return;
        }
        setEmailPendingCount(res.content);
      })
      .catch(() => {
        if (!cancelled) message.error(t('email.pending_reply_failed'));
      });
    return () => {
      cancelled = true;
    };
  }, [shopId, t]);

  useEffect(() => {
    let cancelled = false;
    getAccountQuota()
      .then((res) => {
        if (cancelled) return;
        if (res.code !== 200 || !res.content) {
          if (res.code !== -1) message.error(res.message || t('message.quota_load_failed'));
          setQuota(null);
          return;
        }
        setQuota(res.content);
      })
      .catch(() => {
        if (!cancelled) message.error(t('message.quota_load_failed'));
      });
    return () => {
      cancelled = true;
    };
  }, [t]);

  const planName = account?.membership?.name?.trim() || t('login.plan_pro');
  const planTier = planMemberTier({
    displayName: account?.membership?.name,
    grade: account?.membership?.grade
  });
  const shop = quota?.shop;
  const mail = quota?.mail;
  const dm = quota?.dm;
  const invite = quota?.invite;
  const mailUnlimited = mail?.unlimited === true;
  const mailTotalText = !mail ? '—' : mailUnlimited ? t('workbench.unlimited') : formatFunnelCount(mail.total);
  const shopPercent =
    shop && shop.total > 0 ? Math.min(100, Math.round((shop.used / shop.total) * 100)) : 0;
  const quotaResetAt =
    formatQuotaResetAt(mail?.nextResetAt) ||
    formatQuotaResetAt(dm?.nextResetAt) ||
    formatQuotaResetAt(invite?.nextResetAt);
  const addonItems = [
    { key: 'shop', label: t('workbench.usage_shop'), count: shop?.addonTotal },
    { key: 'dm', label: t('workbench.dm_tag'), count: dm?.addonTotal },
    { key: 'invite', label: t('workbench.usage_invite'), count: invite?.addonTotal },
    { key: 'mail', label: t('workbench.usage_email'), count: mail?.addonTotal }
  ].filter((item): item is { key: string; label: string; count: number } => typeof item.count === 'number' && item.count > 0);

  const todayLabel = useMemo(() => {
    const lang = i18n.language.toLowerCase();
    let locale = 'zh-CN';
    if (lang.startsWith('en')) locale = 'en-US';
    else if (lang.startsWith('vi') || lang === 'vn') locale = 'vi-VN';
    return new Date().toLocaleDateString(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  }, [t, i18n.language]);

  const funnelSteps = useMemo<FunnelStep[]>(
    () => [
      {
        key: 'matchable',
        icon: <SearchOutlined />,
        value: formatFunnelCount(funnel?.matchableCount),
        label: t('workbench.funnel_matchable'),
        meta: t('workbench.funnel_pool'),
        metaTone: 'muted'
      },
      {
        key: 'dm',
        icon: <CommentOutlined />,
        value: formatFunnelCount(funnel?.dmCount),
        label: t('workbench.funnel_dm'),
        meta: t('workbench.funnel_dm_rate', {
          rate: formatFunnelRate(funnel?.dmCount, funnel?.matchableCount)
        }),
        metaTone: 'warn'
      },
      {
        key: 'email',
        icon: <MailOutlined />,
        value: formatFunnelCount(funnel?.emailCount),
        label: t('workbench.funnel_emailed'),
        meta: t('workbench.funnel_dm_to_email', {
          rate: formatFunnelRate(funnel?.emailCount, funnel?.dmCount)
        }),
        metaTone: 'warn'
      },
      {
        key: 'invite',
        icon: <SendOutlined />,
        value: formatFunnelCount(funnel?.invitedCount),
        label: t('workbench.targeted_invite'),
        meta: t('workbench.funnel_invite_rate', {
          rate: formatFunnelRate(funnel?.invitedCount, funnel?.emailCount)
        }),
        metaTone: 'warn'
      },
      {
        key: 'accepted',
        icon: <CheckCircleOutlined />,
        value: formatFunnelCount(funnel?.repliedCount),
        label: t('workbench.funnel_accepted'),
        meta: t('workbench.funnel_total_rate', {
          rate: formatFunnelRate(funnel?.repliedCount, funnel?.matchableCount)
        }),
        metaTone: 'success',
        variant: 'success'
      }
    ],
    [funnel, t]
  );

  const quickEntryItems = useMemo<QuickEntryItem[]>(
    () => [
      { key: 'talent', label: t('layout.menu_talent'), icon: <SolutionOutlined />, path: '/creator/creator_manager' },
      { key: 'creator', label: t('layout.menu_creator'), icon: <SolutionOutlined />, path: '/creator/creator_manager' },
      { key: 'invite', label: t('workbench.targeted_invite'), icon: <SendOutlined />, path: '/creator/invitation' },
      { key: 'dm', label: t('workbench.quick_dm'), icon: <QuestionCircleOutlined />, path: '/creator/private_message' },
      { key: 'email', label: t('workbench.quick_email'), icon: <MailOutlined />, path: '/creator/email' },
      { key: 'script', label: t('layout.menu_script'), icon: <ClockCircleOutlined />, path: '/creator/script' },
      { key: 'shop', label: t('layout.menu_store'), icon: <ShopOutlined />, path: '/creator/store_authorization' },
      { key: 'upgrade', label: t('workbench.quick_upgrade'), icon: <StarOutlined />, path: '/creator/upgrade' }
    ],
    [t]
  );

  const goQuickEntry = (path: string) => {
    navigate(path);
  };

  return (
    <div className="container">
      <header className="mb-5 flex items-center justify-between gap-4">
        <div className="min-w-0">
          <h1 className="mb-2 mt-0 text-[22px] font-semibold leading-snug tracking-tight text-slate-800">
            {t('workbench.today_overview')}
          </h1>
          <p className="m-0 text-sm leading-normal text-slate-500">{workbenchShopLine(selectedShop, t)}</p>
        </div>
        <time className="shrink-0 text-[13px] leading-normal text-slate-400" dateTime={todayLabel}>
          {todayLabel}
        </time>
      </header>

      <div className="grid grid-cols-1 gap-4 min-[641px]:grid-cols-2 min-[1201px]:grid-cols-4">
        <WorkbenchStatCard icon={<ShopOutlined />} title={t('workbench.authorized_shops')} tone="amber">
          <p className="relative z-[1] mb-3 mt-0 flex items-baseline gap-0.5">
            <span
              className={`text-[32px] font-bold leading-none tracking-tight ${statCardToneClass.amber.value}`}
            >
              {formatFunnelCount(shop?.used)}
            </span>
            {/* <span className="text-xl font-semibold text-slate-400">/{formatFunnelCount(shop?.total)}</span> */}
          </p>
          {shop ? <WorkbenchProgress percent={shopPercent} tone="amber" /> : null}
          <div className="relative z-[1] flex flex-wrap items-center gap-2">
            <span
              className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs leading-snug ${statCardToneClass.amber.chip}`}
            >
              {t('workbench.mail_remain', { remain: formatFunnelCount(shop?.remaining) })}
            </span>
            <span className="text-xs leading-normal text-slate-400">
              / {formatFunnelCount(shop?.total)}
              {shop ? `（${t('workbench.addon_included', { extra: formatFunnelCount(shop.addonTotal) })}）` : ''}
            </span>
          </div>
        </WorkbenchStatCard>
        {/* <WorkbenchStatCard icon={<CommentOutlined />} title={t('workbench.pending_replies')} tone="violet">
          <p
            className={`relative z-[1] mb-3 mt-0 text-[32px] font-bold leading-none tracking-tight ${statCardToneClass.violet.value}`}
          >
            {pendingReplyCount == null ? '—' : pendingReplyCount.toLocaleString()}
          </p>
          <div className="relative z-[1] flex flex-wrap gap-2">
            <span
              className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs leading-snug ${statCardToneClass.violet.chip}`}
            >
              <span
                className={`size-2 shrink-0 rounded-sm ${statCardToneClass.violet.chipDot}`}
                aria-hidden
              />
              {t('workbench.dm_chip', { value: pendingReplyCount == null ? '—' : pendingReplyCount })}
            </span>
            <span
              className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs leading-snug ${statCardToneClass.violet.chip}`}
            >
              <span
                className={`size-2 shrink-0 rounded-sm ${statCardToneClass.violet.chipDot}`}
                aria-hidden
              />
              {t('workbench.email_chip', { value: emailPendingCount == null ? '—' : emailPendingCount })}
            </span>
          </div>
        </WorkbenchStatCard> */}
        <WorkbenchPeriodQuota
          icon={<MessageOutlined />}
          title={t('workbench.dm_quota')}
          tone="sky"
          quota={dm}
        />
        <WorkbenchPeriodQuota
          icon={<SendOutlined />}
          title={t('workbench.invite_quota')}
          tone="violet"
          quota={invite}
        />
        <WorkbenchPeriodQuota
          icon={<MailOutlined />}
          title={t('workbench.email_this_month')}
          tone="emerald"
          quota={mail}
        />


      </div>

      <section
        className="wb-section mt-5 flex items-center justify-between gap-5 max-md:flex-col max-md:items-stretch"
        aria-label={t('workbench.plan_benefits')}
      >
        <div className="min-w-0 flex-1">
          <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2">
            <span className={`wb-plan-badge${planTier ? ` wb-plan-badge--${planTier}` : ''}`}>
              <PlanMemberMark
                name={account?.membership?.name}
                grade={account?.membership?.grade}
                className="plan-member-mark plan-member-mark--badge"
              />
              {planName}
            </span>
          </div>
          <p className="m-0 flex items-center gap-1.5 text-[13px] leading-normal text-slate-500">
            <span>{t('workbench.quota_reset_time')}</span>
            <span className="text-slate-700">{quotaResetAt || '—'}</span>
            <Tooltip title={t('workbench.quota_reset_hint')} styles={{ body: { fontSize: 12 } }}>
              <QuestionCircleOutlined className="cursor-help text-slate-400" />
            </Tooltip>
          </p>
        </div>
        <Button
          type="primary"
          className="wb-btn-upgrade wb-package-btn rounded-full"
          onClick={() => navigate('/creator/upgrade')}
        >
          {t('layout.upgrade')}
        </Button>
      </section>

      <section className="wb-section mt-5" aria-label={t('workbench.funnel')}>
        <WorkbenchSectionHeader
          icon={<UngroupOutlined />}
          title={t('workbench.funnel')}
          action={
            <Button
              type="primary"
              size="small"
              className="wb-btn-primary wb-btn-compact"
              icon={<ThunderboltOutlined />}
              onClick={() => setOutreachOpen(true)}
            >
              {t('workbench.quick_outreach')}
            </Button>
          }
        />
        <div className="mt-5">
          <div className="flex items-start gap-0 overflow-x-auto pb-1 [-webkit-overflow-scrolling:touch]">
            {funnelSteps.map((step, index) => (
              <Fragment key={step.key}>
                <WorkbenchFunnelStep step={step} />
                {index < funnelSteps.length - 1 ? (
                  <div
                    className="mt-[34px] h-px shrink-0 basis-[clamp(12px,2.5vw,28px)] bg-[#dce3ed] max-[900px]:basis-4"
                    aria-hidden
                  />
                ) : null}
              </Fragment>
            ))}
          </div>
        </div>
      </section>

      <div className="mt-5 flex items-stretch gap-4 max-md:flex-col">
        <section
          className="wb-section mt-0 flex min-h-[120px] min-w-0 flex-[7_1_0] flex-col max-md:w-full max-md:flex-auto"
          aria-label={t('workbench.todos')}
        >
          <WorkbenchSectionHeader icon={<CarryOutOutlined />} title={t('workbench.todos')} />
          <div className="mt-4 flex min-h-0 flex-1 flex-col gap-3">
            <div className="flex items-center gap-3 rounded-lg border border-[#eef0f4] p-3">
              <span
                className="inline-flex size-12 shrink-0 items-center justify-center rounded-lg bg-[#f0f2ff] text-xl text-[#596ae8]"
                aria-hidden
              >
                <MessageOutlined />
              </span>
              <div className="min-w-0 flex-1">
                <p className="m-0 text-sm font-medium text-slate-800">
                  {t('workbench.pending_dms', { value: pendingReplyCount == null ? '—' : pendingReplyCount })}
                </p>
                {/* <p className="m-0 mt-1 text-xs text-slate-500">{t('workbench.pending_dms_meta')}</p> */}
              </div>
              <span className="rounded-full bg-[#3b4dd7]/10 px-2 py-1 text-xs font-medium text-[#3b4dd7]">
                {t('workbench.dm_tag')}
              </span>
              <button type="button" className="wb-funnel-action" onClick={() => navigate('/creator/private_message')}>
                {t('workbench.handle')}
              </button>
            </div>
            <div className="flex items-center gap-3 rounded-lg border border-[#eef0f4] p-3">
              <span
                className="inline-flex size-12 shrink-0 items-center justify-center rounded-lg bg-[#f0f2ff] text-xl text-[#596ae8]"
                aria-hidden
              >
                <MailOutlined />
              </span>
              <div className="min-w-0 flex-1">
                <p className="m-0 text-sm font-medium text-slate-800">
                  {t('workbench.pending_emails', { value: emailPendingCount == null ? '—' : emailPendingCount })}
                </p>
                {/* <p className="m-0 mt-1 text-xs text-slate-500">{t('workbench.pending_emails_meta')}</p> */}
              </div>
              <span className="rounded-full bg-[#3b4dd7]/10 px-2 py-1 text-xs font-medium text-[#3b4dd7]">
                {t('workbench.email_tag')}
              </span>
              <button type="button" className="wb-funnel-action" onClick={() => navigate('/creator/email')}>
                {t('workbench.handle')}
              </button>
            </div>
          </div>
        </section>

        <section
          className="wb-section mt-0 flex min-h-[120px] min-w-0 flex-[3_1_0] flex-col max-md:w-full max-md:flex-auto"
          aria-label={t('workbench.quick_entry')}
        >
          <WorkbenchSectionHeader icon={<DesktopOutlined />} title={t('workbench.quick_entry')} />
          <div className="mt-4 flex min-h-0 flex-1 flex-col">
            <div className="mt-3 grid flex-1 grid-cols-2 gap-2 content-start">
              {quickEntryItems.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className="flex flex-col items-center gap-2 rounded-lg border border-[#eef0f4] bg-white px-2 py-3 transition hover:border-[#596ae8]/25 hover:bg-[#f8f9ff]"
                  onClick={() => goQuickEntry(item.path)}
                >
                  <span className="inline-flex size-9 items-center justify-center rounded-lg bg-[#f0f2ff] text-base text-[#596ae8]">
                    {item.icon}
                  </span>
                  <span className="text-xs font-medium text-slate-700">{item.label}</span>
                </button>
              ))}
            </div>
            <p className="m-0 mt-auto pt-3 text-center text-[11px] leading-relaxed text-slate-400">
              {t('workbench.quick_hint')}
            </p>
          </div>
        </section>
      </div>
      <QuickOutreachModal open={outreachOpen} onClose={() => setOutreachOpen(false)} />
    </div>
  );
});


